/**
 * The merge-field palette shown beside any subject/body editor. Clicking a chip
 * inserts the token at the caret of the field that was last focused.
 */
import { MERGE_FIELDS, type MergeFieldScope } from '@gwc/shared';

export function insertAtCaret(
  el: HTMLInputElement | HTMLTextAreaElement | null,
  token: string,
  current: string,
): string {
  if (!el) return `${current}${token}`;
  const start = el.selectionStart ?? current.length;
  const end = el.selectionEnd ?? current.length;
  const next = `${current.slice(0, start)}${token}${current.slice(end)}`;
  requestAnimationFrame(() => {
    el.focus();
    el.setSelectionRange(start + token.length, start + token.length);
  });
  return next;
}

export function MergeFieldChips({
  onInsert,
  scopes = ['person', 'club', 'event'],
  eventAttached,
}: {
  onInsert: (token: string) => void;
  scopes?: MergeFieldScope[];
  /** When false, event chips are shown dimmed with a hint. */
  eventAttached?: boolean;
}) {
  const fields = MERGE_FIELDS.filter((f) => scopes.includes(f.scope));
  return (
    <div>
      <p className="mb-1.5 text-[11px] uppercase tracking-wide text-cream-300/50">
        Insert a field
      </p>
      <div className="flex flex-wrap gap-1.5">
        {fields.map((f) => {
          const dim = f.scope === 'event' && eventAttached === false;
          return (
            <button
              key={f.key}
              type="button"
              title={dim ? 'Attach a tasting to use this' : `{{${f.key}}} → e.g. ${f.sample}`}
              onClick={() => onInsert(`{{${f.key}}}`)}
              className={`rounded-full border px-2 py-0.5 font-mono text-[11px] transition ${
                dim
                  ? 'border-ink-800 text-cream-300/30'
                  : 'border-gold-600/40 text-gold-200 hover:border-gold-400 hover:text-gold-400'
              }`}
            >
              {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}
